/**
 * Sample Product Selection Handler
 * Manages adding/removing sample products from cart
 */

class SampleProductHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.bindEvents();
    });
  }

  bindEvents() {
    const sampleSelect = document.querySelector('[data-sample-product-select]');
    
    if (sampleSelect) {
      sampleSelect.addEventListener('change', (e) => {
        this.handleSampleSelection(e.target.value);
      });
    }

    // Handle sample remove buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-sample-remove]')) {
        e.preventDefault();
        this.handleSampleRemove(e.target);
      }
    });
  }

  async handleSampleSelection(variantId) {
    try {
      // Always remove existing samples first
      await this.removeSampleFromCart();
      
      // Small delay to ensure removal completes
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Then add the new sample if one was selected
      if (variantId && variantId !== '0') {
        await this.addSampleToCart(variantId);
      }
      
      // Refresh the cart display
      await this.refreshCart();
      
    } catch (error) {
      console.error('Error handling sample product:', error);
      this.showError('Failed to update sample product. Please try again.');
    }
  }

  async handleSampleRemove(removeButton) {
    try {
      // Show loading state
      const originalText = removeButton.textContent;
      removeButton.classList.add('is-loading');
      removeButton.textContent = 'Poistetaan...';
      
      // Remove the sample from cart
      await this.removeSampleFromCart();
      
      // Update sample selector to "Choose sample..." option
      const sampleSelect = document.querySelector('[data-sample-product-select]');
      if (sampleSelect) {
        sampleSelect.value = '0';
      }
      
      // Refresh the cart display
      await this.refreshCart();
      
    } catch (error) {
      console.error('Error removing sample product:', error);
      this.showError('Failed to remove sample product. Please try again.');
      
      // Reset button state
      removeButton.classList.remove('is-loading');
      removeButton.textContent = originalText;
    }
  }

  async addSampleToCart(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add sample product');
    }
  }

  async removeSampleFromCart() {
    // Get sample product ID from settings
    const sampleProductId = window.theme?.sampleProductId;
    
    console.log('Sample Product ID from settings:', sampleProductId);
    
    if (!sampleProductId) {
      console.warn('Sample product ID not found');
      return;
    }

    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      console.log('Current cart items:', cart.items.map(item => ({
        id: item.id,
        product_id: item.product_id,
        title: item.title,
        variant_title: item.variant_title
      })));
      
      // Find sample product items (all variants of the sample product)
      const itemsToRemove = cart.items.filter(item => item.product_id === sampleProductId);
      
      console.log('Items to remove:', itemsToRemove.map(item => ({
        id: item.id,
        product_id: item.product_id,
        title: item.title
      })));
      
      if (itemsToRemove.length === 0) {
        console.log('No sample items found to remove');
        return; // No samples to remove
      }

      // Remove items one by one using cart/change.js
      // Sort by line number descending to avoid line number shifts
      const itemsWithLineNumbers = itemsToRemove.map(item => ({
        item,
        lineIndex: cart.items.indexOf(item) + 1
      })).sort((a, b) => b.lineIndex - a.lineIndex);

      for (const { item, lineIndex } of itemsWithLineNumbers) {
        console.log(`Removing line ${lineIndex} (${item.title})`);
        
        const changeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 0
          })
        });
        
        console.log(`Remove response status for line ${lineIndex}:`, changeResponse.status);
        
        if (!changeResponse.ok) {
          console.error(`Failed to remove line ${lineIndex}`);
        }
      }

    } catch (error) {
      console.error('Error removing sample products:', error);
    }
  }

  async refreshCart() {
    // Use theme's cart refresh event system
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    
    // Also try other refresh methods as fallbacks
    if (window.theme?.cartDrawer?.refresh) {
      window.theme.cartDrawer.refresh();
    } else if (window.theme?.cart?.refresh) {
      window.theme.cart.refresh();
    }
    
    // Give it a moment to process
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  showError(message) {
    // You can customize this to show errors in your theme's style
    console.error(message);
    
    // Try to show in cart errors if available
    const errorElement = document.querySelector('[data-cart-errors]');
    if (errorElement) {
      const errorMessage = errorElement.querySelector('[data-error-message]');
      if (errorMessage) {
        errorMessage.textContent = message;
        errorElement.style.display = 'block';
      }
    }
  }
}

// Initialize when DOM is ready
new SampleProductHandler(); 